import { PUBLISHING } from "@/constants/commonConstants";

/** Publishing type options on the job profile General Info panel (same labels as `PUBLISHING`). */
export const PROFILE_PUBLISHING_TYPES = [
  PUBLISHING.PRODUCT,
  PUBLISHING.PRODUCT_MAJOR_UPGRADE,
  PUBLISHING.UPDATE,
  PUBLISHING.COMPONENT,
  PUBLISHING.EXTENSION,
  PUBLISHING.LIBRARY,
  PUBLISHING.ENGINE,
  PUBLISHING.LANGUAGE_PACK,
  PUBLISHING.PLUG_IN
] as const;

/** Publishing types that may use Auto release mode on eligible channels. */
export const PROFILE_PUBLISHING_TYPES_ALLOW_AUTO_RELEASE = [
  PUBLISHING.PRODUCT,
  PUBLISHING.PRODUCT_MAJOR_UPGRADE
] as const;

export const profilePublishingTypesAllowAutoRelease = (releaseTypes: string[]): boolean =>
  releaseTypes.some((type) =>
    (PROFILE_PUBLISHING_TYPES_ALLOW_AUTO_RELEASE as readonly string[]).includes(type)
  );

/** Canonical `Profile.Platform` values for job profile API/YAML (title case). */
export const PROFILE_PLATFORM = {
  WINDOWS: "Windows",
  MAC: "Mac",
  LINUX: "Linux"
} as const;

export const PROFILE_PLATFORM_OPTIONS = [
  { label: "Windows", value: PROFILE_PLATFORM.WINDOWS },
  { label: "Mac", value: PROFILE_PLATFORM.MAC },
  { label: "Linux", value: PROFILE_PLATFORM.LINUX }
] as const;

export const PROFILE_SIDEBAR_NODE = {
  GENERAL: "general",
  CHANNEL_INFO: "channel-info",
  REVIEW: "review",
  CHANNEL_PREFIX: "channel:"
} as const;

/** Default item (PLC) selected on the job profiles dashboard. */
export const DEFAULT_PROFILE_DASHBOARD_PLC = "_DEFAULT";

/** JobProfile registry status filter on the dashboard (empty = all). */
export const JOB_PROFILE_STATUS_FILTER_ALL = "";

export const JOB_PROFILE_STATUS_OPTIONS = [
  { label: "All statuses", value: JOB_PROFILE_STATUS_FILTER_ALL },
  // { label: "Draft", value: "Draft" },
  { label: "Live", value: "Live" },
  // { label: "Discontinued", value: "Discontinued" }
] as const;

/** Default parent channel always present on new profiles. */
export const DEFAULT_PROFILE_CHANNEL_NAME = "release";

/** Maximum number of profile owners on the General Info panel. */
export const MAX_PROFILE_OWNERS = 5;

/*
 * Read-only Channel Info fields on the submit profile form (still sent on save when re-enabled).
export const CHANNEL_INFO_LOCKED_FIELD_DEFAULTS = {
  ttl: 0,
  sideBySide: false,
  promotionAllowed: false
} as const;
 */

/** Sidebar / form display labels (prototype-aligned). */
export const CHANNEL_DISPLAY_LABELS: Record<string, string> = {
  release: "Release",
  development: "Development",
  insider: "Insider"
};

export const PROFILE_ENVIRONMENT_OPTIONS = [
  { label: "PRD", value: "prd" },
  { label: "STG", value: "stg" }
] as const;

/** Images to Publish — maps UI labels to profile `Publish.JobType` keys. */
export const PROFILE_PUBLISH_JOB_TYPES = {
  INSTALL: "di",
  CREATE_UPLOAD: "db",
  INGEST_UPLOAD: "ds"
} as const;

/** Radio options under Download Image (only one may be selected). */
export const PROFILE_PUBLISH_DOWNLOAD_CHILD = {
  CREATE_UPLOAD: "create-upload",
  UPLOAD_PREBUILT: "upload-prebuilt",
  INGEST_UPLOAD: "ingest-upload"
} as const;

export type ProfilePublishDownloadChildId =
  (typeof PROFILE_PUBLISH_DOWNLOAD_CHILD)[keyof typeof PROFILE_PUBLISH_DOWNLOAD_CHILD];

export const PROFILE_PUBLISH_DOWNLOAD_CHILD_OPTIONS = [
  {
    id: PROFILE_PUBLISH_DOWNLOAD_CHILD.CREATE_UPLOAD,
    label: "Create and Upload Image",
    jobType: PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD,
    preBuilt: false
  },
  {
    id: PROFILE_PUBLISH_DOWNLOAD_CHILD.UPLOAD_PREBUILT,
    label: "Upload Prebuilt Image",
    jobType: PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD,
    preBuilt: true
  },
  {
    id: PROFILE_PUBLISH_DOWNLOAD_CHILD.INGEST_UPLOAD,
    label: "Ingest and Upload Image",
    jobType: PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD,
    preBuilt: false
  }
] as const;

export const PROFILE_IMAGES_TO_PUBLISH = [
  { label: "Install Image", jobType: PROFILE_PUBLISH_JOB_TYPES.INSTALL },
  {
    label: "Download Image",
    children: PROFILE_PUBLISH_DOWNLOAD_CHILD_OPTIONS
  }
] as const;

export const PROFILE_RELEASE_IMAGE_OPTIONS = [
  { label: "Install Image", jobType: PROFILE_PUBLISH_JOB_TYPES.INSTALL },
  { label: "Create and Upload Image", jobType: PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD },
  { label: "Ingest and Upload Image", jobType: PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD }
] as const;

/** Release states shown in Release Details (required images + sync event). */
export const PROFILE_RELEASE_REQUIRED_STAGES = ["Preview", "Live"] as const;

export type ProfileReleaseRequiredStageName = (typeof PROFILE_RELEASE_REQUIRED_STAGES)[number];

/** Includes Draft for legacy profile YAML; not shown in the submit form. */
export const PROFILE_RELEASE_STAGES = ["Draft", "Preview", "Live"] as const;

export type ProfileReleaseStageName = (typeof PROFILE_RELEASE_STAGES)[number];

/** Channel release mode — matches apps `ReleaseMode` YAML field (`manual`, `disabled`, `auto`). */
export const PROFILE_RELEASE_MODES = ["manual", "disabled", "auto"] as const;

export type ProfileReleaseMode = (typeof PROFILE_RELEASE_MODES)[number];

export const PROFILE_RELEASE_MODE_LABELS: Record<ProfileReleaseMode, string> = {
  manual: "Manual",
  disabled: "Disabled",
  auto: "Auto"
};

export const PROFILE_RELEASE_ALLOWED_IMAGES_TOOLTIP =
  "Select the images allowed for release.";

export const PROFILE_RELEASE_REQUIRED_IMAGES_TOOLTIP =
  "Preview is optional if Live is selected. All allowed images will be released to Preview (with downstream events) but only selected images will be released to Live.";

export const PROFILES_PER_PAGE = 50;

/** Session-storage key prefix for profile list pagination (matches jobs/releases pattern). */
export const PROFILE_TABLE_MODE = "JOB_PROFILES";

export const JOB_PROFILE_ROUTES = {
  LIST: "/jobProfiles",
  CREATE: "/submitJobProfile"
} as const;
